import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { secureLog } from '@/lib/security';

export const useAdminAuth = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAdminStatus();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT') {
          setIsAdmin(false);
          setIsLoading(false);
        } else if (event === 'SIGNED_IN' && session) {
          checkAdminStatus();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Use enhanced security check for admin validation
        const { data: isSecureAdmin, error } = await supabase.rpc('is_current_user_admin_secure');
        
        if (error) {
          secureLog.error('Error checking secure admin status', error);
          setIsAdmin(false);
        } else {
          setIsAdmin(isSecureAdmin || false);
          
          // Log admin status check
          if (isSecureAdmin) {
            await supabase.rpc('log_admin_action', {
              _action_type: 'admin_status_check',
              _details: { timestamp: new Date().toISOString() }
            });
          }
        }
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      secureLog.error('Error checking admin status', error);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    await checkAdminStatus();
    
    // Log admin sign in attempt
    if (data.user) {
      await supabase.rpc('log_admin_action', {
        _action_type: 'admin_signin_attempt',
        _details: { 
          email: email.replace(/(.{3}).*@/, '$1***@'), // Partially masked email
          timestamp: new Date().toISOString() 
        }
      });
    }
    
    return data;
  };

  const signOut = async () => {
    // Log admin sign out
    if (isAdmin) {
      await supabase.rpc('log_admin_action', {
        _action_type: 'admin_signout',
        _details: { timestamp: new Date().toISOString() }
      });
    }
    
    setIsAdmin(false);
    setIsLoading(false);
    await supabase.auth.signOut();
  };

  return {
    isAdmin,
    isLoading,
    signInWithEmail,
    signOut,
    checkAdminStatus
  };
};